import { createSlice } from "@reduxjs/toolkit";

import {
  deleteComment,
  editComment,
  fetchCounts,
  fetchDiscussion,
  fetchMentionCandidates,
  fetchOutlineCounts,
  postBulkComment,
  postComment,
} from "./actions";
import { buildCountsKey, buildDiscussionKey, buildItemRefKey } from "./helpers";

export const initialState = {
  discussions: {},
  counts: {},
  mentionCandidates: [],
  isMentionCandidatesLoading: false,
};

const DEFAULT_ERROR = "Failed to load comments";

const emptyDiscussion = () => ({
  itemLabel: "",
  commentCount: 0,
  threads: [],
  isLoading: false,
  error: null,
});

const discussionKeyFor = ({ surface, containerId, itemRef }) =>
  buildDiscussionKey(surface, containerId, itemRef);

const getOrCreateDiscussion = (state, discussionKey) => {
  if (!state.discussions[discussionKey]) {
    state.discussions[discussionKey] = emptyDiscussion();
  }
  return state.discussions[discussionKey];
};

// Counts are stamped from every mutation response rather than recounted
// locally (contract §5.2) — absent from the map means zero, no badge.
const setCount = (state, surface, containerId, itemRef, commentCount) => {
  const countsKey = buildCountsKey(surface, containerId);
  const itemKey = buildItemRefKey(surface, itemRef);
  if (!state.counts[countsKey]) {
    state.counts[countsKey] = {};
  }
  if (commentCount > 0) {
    state.counts[countsKey][itemKey] = commentCount;
  } else {
    delete state.counts[countsKey][itemKey];
  }
};

const replaceCommentInThreads = (threads, updatedComment) => {
  threads.forEach((thread) => {
    if (thread.comment.id === updatedComment.id) {
      thread.comment = updatedComment;
      return;
    }
    const replyIndex = thread.replies.findIndex(
      (reply) => reply.id === updatedComment.id
    );
    if (replyIndex !== -1) {
      thread.replies[replyIndex] = updatedComment;
    }
  });
};

const commentsSlice = createSlice({
  name: "comments",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchDiscussion.pending, (state, action) => {
        const discussion = getOrCreateDiscussion(
          state,
          discussionKeyFor(action.meta.arg)
        );
        discussion.isLoading = true;
        discussion.error = null;
      })
      .addCase(fetchDiscussion.fulfilled, (state, action) => {
        const discussion = getOrCreateDiscussion(
          state,
          discussionKeyFor(action.meta.arg)
        );
        discussion.isLoading = false;
        discussion.itemLabel = action.payload.itemLabel;
        discussion.commentCount = action.payload.commentCount;
        discussion.threads = action.payload.threads;
      })
      .addCase(fetchDiscussion.rejected, (state, action) => {
        const discussion = getOrCreateDiscussion(
          state,
          discussionKeyFor(action.meta.arg)
        );
        discussion.isLoading = false;
        discussion.error = action.error?.message ?? DEFAULT_ERROR;
      })

      .addCase(fetchCounts.fulfilled, (state, action) => {
        const { surface, containerId } = action.meta.arg;
        const countsKey = buildCountsKey(surface, containerId);
        state.counts[countsKey] = action.payload.items.reduce(
          (acc, { itemRef, commentCount }) => {
            acc[buildItemRefKey(surface, itemRef)] = commentCount;
            return acc;
          },
          {}
        );
      })

      // Each outline section is its own comment container (see OutlineTable),
      // so — unlike fetchCounts, which is scoped to one container — this fans
      // one outline-wide response out across each section's own counts bucket,
      // keyed the same way postComment/deleteComment already key it below.
      .addCase(fetchOutlineCounts.fulfilled, (state, action) => {
        const { surface, items } = action.payload;
        items.forEach(({ itemRef, commentCount }) => {
          setCount(state, surface, itemRef.outlineItemId, itemRef, commentCount);
        });
      })

      .addCase(postComment.fulfilled, (state, action) => {
        const { surface, containerId, itemRef, parentCommentId } =
          action.meta.arg;
        const discussion = getOrCreateDiscussion(
          state,
          discussionKeyFor(action.meta.arg)
        );
        const { comment, commentCount } = action.payload;
        discussion.commentCount = commentCount;
        if (parentCommentId == null) {
          discussion.threads.push({ comment, replies: [] });
        } else {
          const thread = discussion.threads.find(
            (t) => t.comment.id === parentCommentId
          );
          if (thread) {
            thread.replies.push(comment);
          }
        }
        setCount(state, surface, containerId, itemRef, commentCount);
      })

      .addCase(editComment.fulfilled, (state, action) => {
        const discussion = getOrCreateDiscussion(
          state,
          discussionKeyFor(action.meta.arg)
        );
        replaceCommentInThreads(discussion.threads, action.payload);
      })

      .addCase(deleteComment.fulfilled, (state, action) => {
        // Deliberately does not try to locally infer tombstone-vs-removed —
        // the contract says to re-read the thread rather than guess (§7).
        // Only the count, which the response does give authoritatively, is
        // updated here; callers re-dispatch fetchDiscussion for fresh threads.
        const { surface, containerId, itemRef } = action.meta.arg;
        const discussion = getOrCreateDiscussion(
          state,
          discussionKeyFor(action.meta.arg)
        );
        discussion.commentCount = action.payload.commentCount;
        setCount(state, surface, containerId, itemRef, action.payload.commentCount);
      })

      .addCase(postBulkComment.fulfilled, (state, action) => {
        const { surface, containerId } = action.meta.arg;
        action.payload.items.forEach(({ itemRef, commentCount }) => {
          setCount(state, surface, containerId, itemRef, commentCount);
        });
      })

      .addCase(fetchMentionCandidates.pending, (state) => {
        state.isMentionCandidatesLoading = true;
      })
      .addCase(fetchMentionCandidates.fulfilled, (state, action) => {
        state.isMentionCandidatesLoading = false;
        state.mentionCandidates = action.payload;
      })
      .addCase(fetchMentionCandidates.rejected, (state, action) => {
        state.isMentionCandidatesLoading = false;
        state.mentionCandidates = [];
        if (!action.meta.condition) {
          state.error = action.error?.message ?? DEFAULT_ERROR;
        }
      });
  },
});

export default commentsSlice.reducer;
